import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Check, Leaf, FlaskConical, ListOrdered, FlaskRound, Heart } from 'lucide-react';
import { useBox } from '../context/BoxContext';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const typeIcons = {
  grain_free: Leaf,
  hydrolysed: FlaskConical,
};

const typeLabels = {
  grain_free: 'Grain Free',
  hydrolysed: 'Hydrolysed',
};

const lifestageLabels = {
  puppy: 'Puppy',
  adult: 'Adult',
  senior: 'Senior',
};

const breedLabels = {
  large: 'Large Breed',
  small: 'Small Breed',
  all: 'All Breeds',
};

export const ProductCard = ({ product }) => {
  const { addProduct, removeProduct, isProductSelected, isFull } = useBox();
  const isSelected = isProductSelected(product.id);
  const TypeIcon = typeIcons[product.type] || Leaf;

  const hasAccordionContent = product.ingredients || product.analytical_constituents || product.benefits;

  const handleClick = () => {
    if (isSelected) {
      removeProduct(product.id);
    } else if (!isFull) {
      addProduct(product);
    }
  };

  const accordionSections = [
    { key: 'ingredients', label: 'Ingredients', icon: ListOrdered, content: product.ingredients },
    { key: 'analytical', label: 'Analytical Constituents', icon: FlaskRound, content: product.analytical_constituents },
    { key: 'benefits', label: 'Benefits', icon: Heart, content: product.benefits },
  ].filter(s => s.content);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -4 }}
      className={`product-card bg-white rounded-xl border overflow-hidden cursor-pointer h-full flex flex-col ${
        isSelected 
          ? 'border-[#0FA49C] shadow-lg ring-2 ring-[#0FA49C]/20' 
          : 'border-gray-100 shadow-sm hover:shadow-md'
      }`}
      onClick={handleClick}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3.465] overflow-hidden bg-gray-50 flex-shrink-0">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Type Badge */}
        <div className={`absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
          product.type === 'grain_free' ? 'badge-grain-free' : 'badge-hydrolysed'
        }`}>
          <TypeIcon className="w-3 h-3" />
          {typeLabels[product.type]}
        </div>
        
        {/* 100g Sample Badge */}
        <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs font-semibold">
          100g Sample
        </div>
        
        {/* Selection Indicator */}
        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-3 right-3 w-8 h-8 bg-[#0FA49C] rounded-full flex items-center justify-center shadow-lg"
          >
            <Check className="w-5 h-5 text-white" />
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-[#222] text-base mb-2 leading-tight">{product.name}</h3>
        <p className="text-[#666] text-sm mb-3 leading-relaxed">{product.description}</p>
        
        {/* Accordion sections */}
        {hasAccordionContent && (
          <div
            className="mb-3"
            onClick={(e) => e.stopPropagation()}
          >
            <Accordion type="single" collapsible className="w-full">
              {accordionSections.map((section) => (
                <AccordionItem
                  key={section.key}
                  value={section.key}
                  className="border-b border-gray-100 last:border-b-0"
                  data-testid={`accordion-${section.key}-${product.id}`}
                >
                  <AccordionTrigger className="py-2 text-xs font-semibold text-[#444] hover:text-[#0FA49C] hover:no-underline gap-2">
                    <span className="flex items-center gap-1.5">
                      <section.icon className="w-3.5 h-3.5 text-[#0FA49C]" />
                      {section.label}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-[#666] leading-relaxed whitespace-pre-line pb-2 pt-0">
                    {section.content}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Spacer to push tags and button to bottom */}
        <div className="flex-grow"></div>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            product.lifestage === 'puppy' ? 'badge-puppy' :
            product.lifestage === 'adult' ? 'badge-adult' : 'badge-senior'
          }`}>
            {lifestageLabels[product.lifestage]}
          </span>
          {product.breed_size !== 'all' && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              product.breed_size === 'large' ? 'badge-large' : 'badge-small'
            }`}>
              {breedLabels[product.breed_size]}
            </span>
          )}
        </div>

        {/* Add Button */}
        <Button
          variant={isSelected ? "secondary" : "default"}
          className={`w-full rounded-xl font-semibold ${
            isSelected 
              ? 'bg-[#222] text-white hover:bg-[#333]' 
              : 'bg-[#0FA49C] text-white hover:bg-[#0d8f88]'
          }`}
          disabled={!isSelected && isFull}
          data-testid={`add-product-btn-${product.id}`}
        >
          {isSelected ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Selected
            </>
          ) : isFull ? (
            'Box Full'
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Add to Box
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};
